import Vue from 'vue'
import Router from 'vue-router'
import moment from 'moment'
import store from './../store'

import Login from '@/views/Login'
import Callback from '@/views/Callback'
import RestorePassword from '@/views/RestorePassword'
import ActivateAccount from '@/views/ActivateAccount'
import Dashboard from '@/views/Dashboard/Dashboard'
import Default from '@/components/layout/Default'

import DevicesList from '@/views/Devices/DevicesList'
import DeviceCreate from '@/views/Devices/DeviceCreate'
import DeviceEdit from '@/views/Devices/DeviceEdit'
import DeviceAlarmPipelines from '@/views/Devices/components/DeviceAlarmPipelines'
import DeviceSettingsBase from '@/views/Devices/DeviceSettingsBase'

import CreateFleet from '@/views/Fleets/FleetCreate'
import FleetsList from '@/views/Fleets/FleetsList'
import FleetEdit from '@/views/Fleets/FleetEdit'
import FleetSettingsBase from '@/views/Fleets/FleetSettingsBase'
import FleetThresholds from '@/views/Fleets/components/FleetThresholds'
import FleetAlarmPipelines from '@/views/Fleets/components/FleetAlarmPipelines'
import FleetNotificationSettings from '@/views/Fleets/components/FleetNotificationSettings'

import Users from '@/views/Users/Users'
import UserEdit from '@/views/Users/UserEdit'
import UserCreate from '@/views/Users/UserCreate'
import UserSettingsBase from '@/views/Users/UserSettingsBase'
import AssignToOrganizationsAndFleets from '@/views/Users/components/AssignToOrganizationsAndFleets'

import Drivers from '@/views/Drivers/Drivers'
import DriverEdit from '@/views/Drivers/DriverEdit'
import DriverCreate from '@/views/Drivers/DriverCreate'

import VehicleCreate from '@/views/Vehicles/VehicleCreate'
import VehicleEdit from '@/views/Vehicles/VehicleEdit'

import Journeys from '@/views/Journeys/Journeys'
import JourneyReport from '@/views/Journeys/JourneyReport'
import Report from '@/views/Reports/Report'
import SharedEvent from '@/views/SharedEvent/SharedEvent'
import Profile from '@/views/Profile/Profile'

import OrganizationsList from '@/views/Organizations/OrganizationsList'
import OrganizationCreate from '@/views/Organizations/OrganizationCreate'
import OrganizationEdit from '@/views/Organizations/OrganizationEdit'
import OrganizationBrandSettings from '@/views/Organizations/components/OrganizationBrandSettings'
import OrganizationAlarmPipelines from '@/views/Organizations/components/OrganizationAlarmPipelines'
import OrganizationNotificationSettings from '@/views/Organizations/components/OrganizationNotificationSettings'
import OrganizationIntegrations from '@/views/Organizations/components/OrganizationIntegrations.vue'
import OrganizationSettingsBase from '@/views/Organizations/OrganizationSettingsBase'
import Tracking from '@/views/Tracking/Tracking'
import Tree from '@/views/Tree/Tree'

Vue.use(Router)

const isAuthenticated = (to, from, next) => {
    const userSession = store.getters['authModule/getUserSession']

    if (userSession && moment().isBefore(moment.unix(userSession.expires_at))) {
        next()
        return
    }

    store.dispatch('authModule/setUserSession', null)
    next('/')
}

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    routes: [
        {
            path: '',
            name: 'login',
            component: Login
        },
        {
            path: '/forgot-password/:restorePasswordKey',
            name: 'restorePassword',
            component: RestorePassword
        },
        {
            path: '/activate-account/:activationKey',
            name: 'activateAccount',
            component: ActivateAccount
        },
        {
            path: '/shared-event/:sharedUri',
            name: 'sharedEvent',
            component: SharedEvent
        },
        {
            path: '/callback',
            name: 'callback',
            component: Callback
        },
        {
            path: '/',
            name: 'default',
            component: Default,
            children: [
                {
                    path: 'dashboard',
                    name: 'dashboard',
                    component: Tracking
                },
                {
                    path: 'dashboard/:vehicleId/:journeyId/:date',
                    name: 'dashboard',
                    component: Tracking
                },
                {
                    path: 'settings',
                    alias: ['settings/devices'],
                    name: 'devicesMain',
                    component: DevicesList
                },
                {
                    path: 'settings/devices/create',
                    name: 'createDevice',
                    component: DeviceCreate
                },
                {
                    path: 'settings/devices/edit/:deviceUuid',
                    component: DeviceSettingsBase,
                    children: [
                        {
                            path: '',
                            name: 'editDevice',
                            component: DeviceEdit
                        },
                        {
                            path: 'alarm-pipelines',
                            name: 'deviceAlarmPipelines',
                            component: DeviceAlarmPipelines
                        }
                    ]
                },
                {
                    path: 'settings/fleets',
                    name: 'fleets',
                    component: FleetsList
                },
                {
                    path: 'settings/fleets/create',
                    name: 'createFleet',
                    component: CreateFleet
                },
                {
                    path: 'settings/fleets/edit/:fleetUuid',
                    component: FleetSettingsBase,
                    children: [
                        {
                            path: '/',
                            name: 'editFleetDetails',
                            component: FleetEdit
                        },
                        {
                            path: 'threshold-settings',
                            name: 'fleetThresholdSettings',
                            component: FleetThresholds
                        },
                        {
                            path: 'notification-settings',
                            name: 'fleetNotificationSettings',
                            component: FleetNotificationSettings
                        },
                        {
                            path: 'alarm-pipelines',
                            name: 'fleetAlarmPipelines',
                            component: FleetAlarmPipelines
                        }
                    ]
                },
                {
                    path: 'settings/fleets/:fleetUuid/vehicle/create',
                    name: 'createVehicle',
                    component: VehicleCreate
                },
                {
                    path: 'settings/fleets/:fleetUuid/vehicle/edit/:vehicleUuid',
                    name: 'editVehicle',
                    component: VehicleEdit
                },
                {
                    path: 'settings/users',
                    name: 'users',
                    component: Users
                },
                {
                    path: 'settings/users/create',
                    name: 'createUsers',
                    component: UserCreate
                },
                {
                    path: 'settings/users/edit/:id',
                    component: UserSettingsBase,
                    children: [
                        {
                            path: '/',
                            name: 'editUserDetails',
                            component: UserEdit
                        },
                        {
                            path: 'assign-user',
                            name: 'assignUserToOrganizationsAndFleets',
                            component: AssignToOrganizationsAndFleets
                        }
                    ]
                },
                {
                    path: 'settings/drivers',
                    name: 'drivers',
                    component: Drivers
                },
                {
                    path: 'settings/drivers/create',
                    name: 'createDrivers',
                    component: DriverCreate
                },
                {
                    path: 'settings/drivers/edit/:id',
                    name: 'editDrivers',
                    component: DriverEdit
                },
                {
                    path: 'journeys-report',
                    name: 'journeyReport',
                    component: JourneyReport
                },
                {
                    path: 'journeys/:vehicleUuid',
                    name: 'journeys',
                    component: Journeys
                },
                {
                    path: 'journeys/:vehicleUuid/:journeyUuid/:date',
                    name: 'historyJourney',
                    component: Journeys
                },
                {
                    path: 'report',
                    name: 'report',
                    component: Report
                },
                {
                    path: 'profile',
                    name: 'profile',
                    component: Profile
                },
                {
                    path: 'settings/organizations/edit/:organizationUuid/',
                    component: OrganizationSettingsBase,
                    children: [
                        {
                            path: '/',
                            name: 'editOrganizationDetails',
                            component: OrganizationEdit
                        },
                        {
                            path: 'brand-settings',
                            name: 'organizationBrandSettings',
                            component: OrganizationBrandSettings
                        },
                        {
                            path: 'notification-settings',
                            name: 'organizationNotificationSettings',
                            component: OrganizationNotificationSettings
                        },
                        {
                            path: 'alarm-pipelines',
                            name: 'organizationAlarmPipelines',
                            component: OrganizationAlarmPipelines
                        },
                        {
                            path: 'tree-view',
                            name: 'tree',
                            component: Tree
                        },
                        {
                            path: 'integrations',
                            name: 'organizationIntegrations',
                            component: OrganizationIntegrations
                        }
                    ]
                },
                {
                    path: 'settings/organizations',
                    name: 'organizations',
                    component: OrganizationsList
                },
                {
                    path: 'settings/organizations/create',
                    name: 'createOrganization',
                    component: OrganizationCreate
                }
            ],
            beforeEnter: isAuthenticated
        },
        {
            path: '*',
            components: Dashboard
        }
    ]
})

router.beforeEach((to, from, next) => {
    const userSession = store.getters['authModule/getUserSession']

    if ('sharedEvent' === to.name) {
        next()
        return
    }

    if (!userSession && 'restorePassword' === to.name) {
        next()
        return
    }
    if (!userSession && 'activateAccount' === to.name) {
        next()
        return
    }

    if (!userSession && 'callback' === to.name) {
        next()
        return
    }

    if (!userSession && 'login' !== to.name) {
        next('/')
        return
    }

    if (userSession && 'login' === to.name && null == from.name) {
        const lastRoute = localStorage.getItem('lastRoute')

        if (null == lastRoute || '/' === lastRoute) {
            next('/dashboard')
        } else {
            next(lastRoute)
        }
        return
    }

    next()
})

router.afterEach((to, from) => {
    if ('sharedEvent' === to.name) {
        return
    }
    localStorage.setItem('lastRoute', to.path)
})

export default router
